import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { LocationAutocomplete } from '@/components/LocationAutocomplete/LocationAutocomplete'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'

import type { GooglePlace } from '../utils/applyGooglePlaceToForm'

import { BusinessLocationField } from './BusinessLocationField'

interface SubjectInfoFieldsProps {
  onPlaceSelected: (place: GooglePlace) => void
  isEmailRequired?: boolean
}

export const SubjectInfoFields = ({
  onPlaceSelected,
  isEmailRequired = false,
}: SubjectInfoFieldsProps) => {
  const { t } = useTranslation()
  const { control } = useFormContext()

  return (
    <div className="flex flex-col gap-4 lg:flex-1">
      <p className="text-base font-medium text-neutralColor lg:text-xl">
        {t('dashboard.runReport.subjectInfoFields.title')}
      </p>
      <div className="flex flex-col gap-3 md:flex-row md:gap-6">
        <FormField
          control={control}
          name="firstName"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormLabel className="sr-only">
                {t('dashboard.runReport.subjectInfoFields.firstName')}
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t(
                    'dashboard.runReport.subjectInfoFields.firstNamePlaceholder',
                  )}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="middleName"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormLabel className="sr-only">
                {t('dashboard.runReport.subjectInfoFields.middleName')}
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t(
                    'dashboard.runReport.subjectInfoFields.middleNamePlaceholder',
                  )}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="lastName"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormLabel className="sr-only">
                {t('dashboard.runReport.subjectInfoFields.lastName')}
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t(
                    'dashboard.runReport.subjectInfoFields.lastNamePlaceholder',
                  )}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <div className="flex flex-col gap-3 md:flex-row md:gap-6">
        <FormField
          control={control}
          name="alias"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormLabel className="sr-only">
                {t('dashboard.runReport.subjectInfoFields.alias')}
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t(
                    'dashboard.runReport.subjectInfoFields.aliasPlaceholder',
                  )}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="dob"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormLabel className="sr-only">
                {t('dashboard.runReport.subjectInfoFields.age')}
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t(
                    'dashboard.runReport.subjectInfoFields.agePlaceholder',
                  )}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={control}
        name="email"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="sr-only">
              {t('dashboard.runReport.subjectInfoFields.email')}
            </FormLabel>
            <FormControl>
              <Input
                {...field}
                type="email"
                placeholder={t(
                  isEmailRequired
                    ? 'dashboard.runReport.subjectInfoFields.emailPlaceholderRequired'
                    : 'dashboard.runReport.subjectInfoFields.emailPlaceholder',
                )}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="countryLongName"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="sr-only">
              {t('dashboard.runReport.subjectInfoFields.location')}
            </FormLabel>
            <FormControl>
              <LocationAutocomplete
                onPlaceSelected={onPlaceSelected}
                defaultValue={field.value}
                placeholder={t(
                  'dashboard.runReport.subjectInfoFields.locationPlaceholder',
                )}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <BusinessLocationField />
    </div>
  )
}
